/**
 * `?` key toggles a route-aware keyboard shortcuts panel anchored to the
 * top-right corner. Page stays interactive — no modal backdrop. Click
 * outside the panel or press Escape / `?` to close.
 *
 * Adapted from `Loveable v1/src/components/KeyboardShortcutsOverlay.tsx`:
 * framer-motion → motion. No FontAwesome dependency; this one was
 * icon-free already.
 */

import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useLocation } from "react-router-dom";
import { keyboardShortcuts as content } from "@/content/ui";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface ShortcutRow {
  keys: string[];
  label: string;
}

function KeyRow({ keys, label }: ShortcutRow) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex w-24 flex-shrink-0 items-center justify-end gap-1">
        {keys.map((k, i) => (
          <kbd
            key={i}
            className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded px-1.5 font-mono text-fs-label tracking-wider"
            style={{
              background: "hsl(var(--secondary) / 0.6)",
              border: "1px solid hsl(var(--border) / 0.5)",
              color: "hsl(var(--foreground) / 0.85)",
              boxShadow: "0 1px 2px hsl(var(--background) / 0.3)",
            }}
          >
            {k}
          </kbd>
        ))}
      </div>
      <span className="font-mono text-fs-label tracking-wide" style={{ color: "hsl(var(--foreground) / 0.7)" }}>
        {label}
      </span>
    </div>
  );
}

function SectionHeading({ label }: { label: string }) {
  return (
    <p
      className="mt-3 mb-1 pb-1 font-mono text-fs-label tracking-[0.25em] uppercase"
      style={{
        color: "hsl(var(--primary) / 0.8)",
        borderBottom: "1px solid hsl(var(--border) / 0.2)",
      }}
    >
      {label}
    </p>
  );
}

export function KeyboardShortcutsOverlay({ open, onClose }: Props) {
  const location = useLocation();
  const isWorkshopPage = /^\/workshop\//.test(location.pathname);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "?") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("[data-shortcuts-toggle]")) return;
      if (panelRef.current && !panelRef.current.contains(target)) onClose();
    };
    document.addEventListener("keydown", handleKey, true);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("keydown", handleKey, true);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, scale: 0.95, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -8 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="fixed top-14 right-4 z-[101] w-[min(20rem,calc(100vw-2rem))] overflow-hidden rounded-lg"
          style={{
            background: "hsl(var(--background) / 0.92)",
            backdropFilter: "blur(20px) saturate(1.4)",
            WebkitBackdropFilter: "blur(20px) saturate(1.4)",
            border: "1px solid hsl(var(--border) / 0.5)",
            boxShadow:
              "0 8px 32px hsl(var(--background) / 0.5), 0 0 0 1px hsl(var(--border) / 0.1), 0 0 24px hsl(var(--primary) / 0.06)",
          }}
        >
          {/* Title */}
          <div
            className="text-foreground flex items-center gap-2 px-4 py-2 font-mono text-fs-label tracking-[0.3em] uppercase"
            style={{ borderBottom: "1px solid hsl(var(--border) / 0.25)" }}
          >
            {content.overlayTitle}
          </div>

          {/* Shortcuts list */}
          <div className="px-4 py-2">
            {isWorkshopPage ? (
              <>
                <SectionHeading label={content.workshopHeading} />
                <KeyRow keys={["Esc"]} label={content.escBack} />
                <KeyRow keys={["1", "–", "8"]} label={content.numSelectPanel} />
              </>
            ) : (
              <>
                <SectionHeading label={content.landingHeading} />
                <KeyRow keys={["Esc"]} label={content.escDeselect} />
                <KeyRow keys={["1", "–", "8"]} label={content.numSelectWorkshop} />
              </>
            )}
            <SectionHeading label={content.globalHeading} />
            <KeyRow keys={["?"]} label={content.questionMark} />
            <KeyRow keys={["Ctrl", "K"]} label={content.repositorySearch} />
          </div>

          {/* Footer */}
          <div
            className="px-4 py-2 text-center"
            style={{ borderTop: "1px solid hsl(var(--border) / 0.2)" }}
          >
            <span className="font-mono text-fs-micro tracking-widest" style={{ color: "hsl(var(--muted-foreground) / 0.6)" }}>
              {content.closeLabel}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
